import { useRef, useState, type ReactNode, type TouchEvent as ReactTouchEvent } from "react";

interface Props {
  children: ReactNode;
  onDelete: () => void;
  /** Fired when the row is tapped (and not currently revealed). */
  onTap?: () => void;
  /** Tailwind bg class to match the row's resting background. */
  bgClass?: string;
}

const REVEAL_THRESHOLD = 60;
const REVEAL_OFFSET = -88;

export default function SwipeableRow({ children, onDelete, onTap, bgClass = "bg-slate-900" }: Props) {
  const startX = useRef<number | null>(null);
  const startY = useRef<number | null>(null);
  const isHorizontal = useRef<boolean | null>(null);
  const baseDx = useRef(0);
  const [dx, setDx] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [animating, setAnimating] = useState(false);

  function onTouchStart(e: ReactTouchEvent) {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isHorizontal.current = null;
    baseDx.current = revealed ? REVEAL_OFFSET : 0;
    setAnimating(false);
  }

  function onTouchMove(e: ReactTouchEvent) {
    if (startX.current === null || startY.current === null) return;
    const ddx = e.touches[0].clientX - startX.current;
    const ddy = e.touches[0].clientY - startY.current;

    if (isHorizontal.current === null) {
      if (Math.abs(ddx) < 8 && Math.abs(ddy) < 8) return;
      isHorizontal.current = Math.abs(ddx) > Math.abs(ddy);
    }
    if (!isHorizontal.current) return;

    const next = baseDx.current + ddx;
    setDx(Math.min(20, Math.max(-160, next)));
  }

  function onTouchEnd() {
    setAnimating(true);
    if (dx <= -REVEAL_THRESHOLD) {
      setRevealed(true);
      setDx(REVEAL_OFFSET);
    } else {
      setRevealed(false);
      setDx(0);
    }
    startX.current = null;
    startY.current = null;
    isHorizontal.current = null;
  }

  function onRowClick() {
    if (revealed) {
      setAnimating(true);
      setRevealed(false);
      setDx(0);
    } else if (onTap) {
      onTap();
    }
  }

  function onDeleteClick(e: React.MouseEvent) {
    e.stopPropagation();
    setAnimating(true);
    setDx(-400);
    setTimeout(onDelete, 180);
  }

  return (
    <div className="relative overflow-hidden">
      <button
        type="button"
        onClick={onDeleteClick}
        className="absolute inset-y-0 right-0 flex items-center justify-center px-4 bg-rose-600 text-white text-sm font-medium"
        style={{ width: Math.abs(REVEAL_OFFSET) }}
        tabIndex={revealed ? 0 : -1}
        aria-hidden={!revealed}
      >
        Delete
      </button>
      <div
        className={bgClass}
        style={{
          transform: `translateX(${dx}px)`,
          transition: animating ? "transform 0.18s ease-out" : "none",
          touchAction: "pan-y"
        }}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={onRowClick}
      >
        {children}
      </div>
    </div>
  );
}
